import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../firebase';
import {
  ApprovedSpecializationCatalogEntry,
  ApprovedSpecializationCatalogIndex,
  buildApprovedSpecializationCatalogIndex,
} from '../utils/specializationCatalog';

interface ApprovedSpecializationCatalogResult {
  entries: ApprovedSpecializationCatalogEntry[];
  index: ApprovedSpecializationCatalogIndex;
}

const APPROVED_SPECIALIZATION_CACHE_TTL_MS = 5 * 60 * 1000;

let approvedSpecializationCatalogCache: {
  expiresAt: number;
  value: ApprovedSpecializationCatalogResult;
} | null = null;

const isRuntimeApprovedAndActive = (data: Record<string, unknown>): boolean => {
  const status = String(data.status || '')
    .trim()
    .toLowerCase();
  if (status !== 'approved') return false;
  if (data.isActive === false) return false;
  return true;
};

export const getApprovedSpecializationCatalog = async (options?: {
  forceRefresh?: boolean;
}): Promise<ApprovedSpecializationCatalogResult> => {
  const shouldUseCache =
    options?.forceRefresh !== true &&
    approvedSpecializationCatalogCache &&
    approvedSpecializationCatalogCache.expiresAt > Date.now();

  if (shouldUseCache && approvedSpecializationCatalogCache) {
    return approvedSpecializationCatalogCache.value;
  }

  const snapshot = await getDocs(
    query(collection(firestore, 'specializations'), where('status', '==', 'approved'))
  );

  const entries = snapshot.docs
    .map((docSnap) => {
      const data = docSnap.data() as Record<string, unknown>;
      if (!isRuntimeApprovedAndActive(data)) return null;

      const name = String(data.name || '')
        .trim()
        .replace(/\s+/g, ' ');
      const category = String(data.category || '')
        .trim()
        .replace(/\s+/g, ' ');
      if (!name || !category) return null;

      return {
        id: docSnap.id,
        name,
        category,
      } as ApprovedSpecializationCatalogEntry;
    })
    .filter((entry): entry is ApprovedSpecializationCatalogEntry => Boolean(entry));

  const index = buildApprovedSpecializationCatalogIndex(entries);

  const value: ApprovedSpecializationCatalogResult = {
    entries: index.entries,
    index,
  };

  approvedSpecializationCatalogCache = {
    expiresAt: Date.now() + APPROVED_SPECIALIZATION_CACHE_TTL_MS,
    value,
  };

  return value;
};

export const clearApprovedSpecializationCatalogCache = (): void => {
  approvedSpecializationCatalogCache = null;
};
